<app-header-toolbar
  [userDisplayName]="getUserDisplayName()"
  (logout)="logout()">
</app-header-toolbar>

<div class="page">
  <!-- Loading -->
  <div class="state-block" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading quote information…</p>
  </div>

  <!-- Quote detail -->
  <main class="wrap" *ngIf="quote && !loading">

    <!-- Hero -->
    <div class="quote-hd">
      <div class="quote-hd-main">
        <p class="eyebrow">
          Quote detail<span *ngIf="quote.programName"> · {{ quote.programName }}</span>
        </p>
        <h1 class="hero-title">
          <span class="id-num">Q-{{ quote.id.toString().padStart(6, '0') }}</span>
          <span class="badge" [ngClass]="getStatusBadgeClass(quote.status)">
            {{ getStatusDisplayName(quote.status) }}
          </span>
        </h1>
      </div>

      <div class="quote-actions">
        <button type="button" class="cta" (click)="emailQuote()">
          <mat-icon aria-hidden="true">send</mat-icon>
          Send to customer
        </button>
        <button
          type="button"
          class="btn-ghost"
          (click)="downloadPdf()"
          [disabled]="pdfDownloading">
          <mat-icon aria-hidden="true">picture_as_pdf</mat-icon>
          {{ pdfDownloading ? 'Preparing…' : 'View PDF' }}
        </button>
        <button type="button" class="btn-ghost" (click)="editQuote()">
          <mat-icon aria-hidden="true">edit</mat-icon>
          Edit quote
        </button>
        <button type="button" class="btn-ghost" (click)="goBack()">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          Back to dashboard
        </button>
      </div>
    </div>

    <!-- Financing headline band -->
    <section class="hero-band" aria-label="Financing headline">
      <div class="hero-band-l mono">Total premium</div>
      <div class="hero-band-big">{{ formatWhole(getTotalPremium()) }}</div>
      <p class="hero-band-line">
        <span class="mono">{{ formatWhole(getTotalDownPayment()) }}</span> down
        · <span class="mono">{{ formatWhole(getAmountFinanced()) }}</span> financed
        · <span class="mono">{{ formatWhole(getTotalPaid()) }}</span> paid to date
      </p>
    </section>

    <!-- Financing summary figures -->
    <div class="fin">
      <div class="fin-cap mono">
        <mat-icon aria-hidden="true">account_balance</mat-icon>
        Financing summary
      </div>
      <div class="fin-grid">
        <div class="fin-cell major">
          <div class="fin-lab">Amount financed</div>
          <div class="fin-val mono">{{ formatCurrency(getAmountFinanced()) }}</div>
        </div>
        <div class="fin-cell major">
          <div class="fin-lab">Down payment</div>
          <div class="fin-val mono">{{ formatCurrency(getTotalDownPayment()) }}</div>
        </div>
        <div class="fin-cell minor" *ngIf="getMonthlyPaymentAmount() > 0">
          <div class="fin-lab">Monthly payment</div>
          <div class="fin-val mono">{{ formatCurrency(getMonthlyPaymentAmount()) }}<span class="unit">/mo</span></div>
        </div>
        <div class="fin-cell minor">
          <div class="fin-lab">Total paid</div>
          <div class="fin-val mono">{{ formatCurrency(getTotalPaid()) }}</div>
        </div>
        <div class="fin-cell major">
          <div class="fin-lab">Remaining balance</div>
          <div class="fin-val mono">{{ formatCurrency(getRemainingBalance()) }}</div>
        </div>
      </div>
    </div>

    <!-- Flight-path timeline (signature) -->
    <section class="sig" *ngIf="isFinanced()" aria-label="Payment flight path">
      <h2>Payment flight path</h2>
      <p class="sig-hint">The down payment is the takeoff; each recorded payment climbs toward paid-in-full.</p>
      <div class="path">
        <svg class="path-line" viewBox="0 0 600 128" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="flightGradView" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" class="grad-start"/>
              <stop offset="1" class="grad-end"/>
            </linearGradient>
          </defs>
          <line x1="30" y1="102" x2="570" y2="29" stroke="url(#flightGradView)" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <div
          *ngFor="let m of getFlightPathMarkers()"
          class="mk"
          [class.takeoff]="m.type === 'takeoff'"
          [class.end]="m.type === 'end'"
          [style.left.%]="m.left"
          [style.bottom.%]="m.bottom">
          <span class="mk-lab mono" *ngIf="m.label">{{ m.label }}</span>
        </div>
      </div>
      <div class="ends">
        <span><b class="mono">{{ formatWhole(getTotalDownPayment()) }}</b> down</span>
        <span class="mid mono">{{ formatWhole(getTotalPaid()) }} paid</span>
        <span><b class="mono">{{ formatWhole(getRemainingBalance()) }}</b> remaining</span>
      </div>
    </section>

    <!-- Customer information -->
    <div class="card">
      <div class="card-head">
        <mat-icon aria-hidden="true">person</mat-icon>
        <h2>Customer information</h2>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <div class="info-k">Insured name</div>
            <div class="info-v">{{ quote.insuredName }}</div>
          </div>
          <div class="info-item">
            <div class="info-k">Agent name</div>
            <div class="info-v">{{ quote.agentName }}</div>
          </div>
          <div class="info-item" *ngIf="quote.email">
            <div class="info-k">Email</div>
            <div class="info-v">{{ quote.email }}</div>
          </div>
          <div class="info-item" *ngIf="quote.phone">
            <div class="info-k">Phone</div>
            <div class="info-v mono">{{ quote.phone }}</div>
          </div>
          <div class="info-item" *ngIf="quote.mobile">
            <div class="info-k">Mobile</div>
            <div class="info-v mono">{{ quote.mobile }}</div>
          </div>
          <div class="info-item" *ngIf="quote.customerNumber">
            <div class="info-k">Customer number</div>
            <div class="info-v mono">{{ quote.customerNumber }}</div>
          </div>
          <div class="info-item" *ngIf="quote.addressLine1">
            <div class="info-k">Address</div>
            <div class="info-v">{{ quote.addressLine1 }}<span *ngIf="quote.addressLine2">, {{ quote.addressLine2 }}</span><span *ngIf="quote.city">, {{ quote.city }}</span><span *ngIf="quote.state">, {{ quote.state }}</span><span *ngIf="quote.postalCode"> {{ quote.postalCode }}</span></div>
          </div>
          <div class="info-item">
            <div class="info-k">Program</div>
            <div class="info-v">{{ quote.programName || 'N/A' }}</div>
          </div>
          <div class="info-item">
            <div class="info-k">Created date</div>
            <div class="info-v mono">{{ formatDate(quote.createdAt) }}</div>
          </div>
          <div class="info-item">
            <div class="info-k">Updated date</div>
            <div class="info-v mono">{{ formatDate(quote.updatedAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Policies -->
    <div class="card" *ngIf="quote.policies && quote.policies.length > 0">
      <div class="card-head">
        <mat-icon aria-hidden="true">policy</mat-icon>
        <h2>Policies</h2>
        <span class="card-badge">{{ quote.policies.length }} {{ quote.policies.length === 1 ? 'policy' : 'policies' }}</span>
      </div>
      <div class="card-body">
        <div class="policy-list">
          <div class="policy-item" *ngFor="let policy of quote.policies">
            <div class="policy-head">
              <div class="policy-no">
                <span class="plabel">Policy</span>
                <span class="mono">#{{ policy.policyNumber }}</span>
              </div>
              <div class="policy-prem mono">{{ formatCurrency(policy.totalPremium) }}</div>
            </div>
            <div class="policy-grid">
              <div class="policy-detail">
                <div class="pd-k">Coverage</div>
                <div class="pd-v">{{ getCoverageDisplayName(policy.coverage) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Effective date</div>
                <div class="pd-v mono">{{ formatDate(policy.effectiveDate) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Expiration date</div>
                <div class="pd-v mono">{{ formatDate(policy.expirationDate) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Premium</div>
                <div class="pd-v mono">{{ formatCurrency(policy.premiumAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Taxes</div>
                <div class="pd-v mono">{{ formatCurrency(policy.taxesAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Fees</div>
                <div class="pd-v mono">{{ formatCurrency(policy.feesAmount) }}</div>
              </div>
              <div class="policy-detail">
                <div class="pd-k">Down payment</div>
                <div class="pd-v mono">{{ formatCurrency(policy.downAmount) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment history -->
    <div class="card">
      <div class="card-head">
        <mat-icon aria-hidden="true">payment</mat-icon>
        <h2>Payment history</h2>
      </div>
      <div class="card-body">
        <div class="table-wrap" *ngIf="quote.payments && quote.payments.length > 0">
          <table mat-table [dataSource]="quote.payments" class="payments-table">
            <!-- Payment Date Column -->
            <ng-container matColumnDef="paymentDate">
              <th mat-header-cell *matHeaderCellDef>Payment Date</th>
              <td mat-cell *matCellDef="let payment" class="fig">{{ formatDate(payment.paymentDate) }}</td>
            </ng-container>

            <ng-container matColumnDef="paymentType">
              <th mat-header-cell *matHeaderCellDef>Type</th>
              <td mat-cell *matCellDef="let payment">{{ getPaymentTypeDisplayName(payment.paymentType) }}</td>
            </ng-container>

            <!-- Stripe Payment ID Column -->
            <ng-container matColumnDef="stripePaymentId">
              <th mat-header-cell *matHeaderCellDef>Stripe Payment ID</th>
              <td mat-cell *matCellDef="let payment">
                <span *ngIf="payment.stripeInvoiceUrl; else paymentIdOnly">
                  <a [href]="payment.stripeInvoiceUrl" target="_blank" rel="noopener noreferrer" class="stripe-link">
                    <mat-icon aria-hidden="true">receipt</mat-icon>
                    View Invoice
                  </a>
                </span>
                <ng-template #paymentIdOnly>
                  {{ payment.stripePaymentId || 'N/A' }}
                </ng-template>
              </td>
            </ng-container>

            <!-- Status Column -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let payment">
                <span class="badge" [ngClass]="getPaymentStatusClass(payment.status)">{{ payment.status }}</span>
              </td>
            </ng-container>

            <!-- Amount Column -->
            <ng-container matColumnDef="amount">
              <th mat-header-cell *matHeaderCellDef class="num">Amount</th>
              <td mat-cell *matCellDef="let payment" class="num">{{ formatCurrency(payment.amount) }}</td>
            </ng-container>

            <!-- Net Amount Column -->
            <ng-container matColumnDef="netAmount">
              <th mat-header-cell *matHeaderCellDef class="num">Net Amount</th>
              <td mat-cell *matCellDef="let payment" class="num">{{ formatCurrency(payment.netAmount) }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="paymentsColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: paymentsColumns;"></tr>
          </table>
        </div>

        <div class="no-payments" *ngIf="!quote.payments || quote.payments.length === 0">
          <mat-icon aria-hidden="true">info</mat-icon>
          <p>No payments have been recorded for this quote yet.</p>
        </div>
      </div>
    </div>

  </main>
</div>
